const API_ENDPOINT =
  "https://get-products-frontend-998838505944.europe-west6.run.app";
const SAVE_ENDPOINT =
  "https://save-processed-product-frontend-998838505944.us-central1.run.app";
const API_ATTR_OPTIONS =
  "https://get-attribute-options-frontend-998838505944.europe-west6.run.app";
const REPROCESS_ENDPOINT =
  "https://reprocess-products-frontend-998838505944.europe-west6.run.app";

export const productService = {
  async fetchCategoryTree(dbName = "test") {
    const url = `${API_ATTR_OPTIONS}?collectionName=processed_products&dbName=${dbName}&categoryTree=true`;
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(
        `fetchCategoryTree error: ${resp.status} ${resp.statusText}`
      );
    }
    const data = await resp.json();
    return data.data.categoryChains || [];
  },

  async fetchRawProductById(productId, dbName = "test") {
    const payload = {
      page: 1,
      pageSize: 1,
      collectionName: "raw_products",
      topFilters: [{ key: "id", value: productId }],
      dbName,
    };

    const resp = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      throw new Error(`Raw fetch error: ${resp.status} ${resp.statusText}`);
    }
    const data = await resp.json();
    if (!data.products?.length) {
      return null;
    }
    return data.products[0];
  },

  async fetchProcessedProductByArticleId(articleId, dbName = "test") {
    const payload = {
      page: 1,
      pageSize: 1,
      collectionName: "processed_products",
      topFilters: [{ key: "id", value: articleId }],
      dbName,
    };

    const resp = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      throw new Error(
        `Processed fetch error (articleId): ${resp.status} ${resp.statusText}`
      );
    }
    const data = await resp.json();
    if (!data.products?.length) {
      return null;
    }
    return data.products[0];
  },

  async fetchProcessedProductByVariantId(variantId, dbName = "test") {
    const payload = {
      page: 1,
      pageSize: 1,
      collectionName: "processed_products",
      variantId,
      dbName,
    };

    const resp = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      throw new Error(
        `Processed fetch error (variantId): ${resp.status} ${resp.statusText}`
      );
    }
    const data = await resp.json();
    if (!data.products?.length) {
      return null;
    }
    return data.products[0];
  },

  async fetchProcessedProductById(productId, dbName) {
    return this.fetchProcessedProductByArticleId(productId, dbName);
  },

  async fetchCatalogProducts(payload) {
    // ✅ This signature is now complete and robust
    const {
      selectedFilters = {},
      removeMissingImages = false,
      brandSearch = "",
      alliumSearch = "",
      webCategorySearch = "",
      selectedAttrId = "",
      selectedAttrValue = "",
      attrExists = null,
      currentPage = 1,
      pageSize = 20,
      dbName = "test",
      showAttributeFlags = false,
    } = payload;

    const topFilters = [];
    for (const [key, values] of Object.entries(selectedFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          topFilters.push({ key, value });
        });
      }
    }

    const apiPayload = {
      collectionName: "processed_products",
      page: currentPage,
      pageSize,
      topFilters,
      removeMissingImages,
      brandSearch,
      alliumSearch,
      webCategorySearch,
      dbName,
      attrName: selectedAttrId,
      attrValue: selectedAttrValue,
      attrExists,
      showAttributeFlags,
    };

    const resp = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(apiPayload),
    });

    if (!resp.ok) {
      const errorBody = await resp.text();
      throw new Error(
        `Fetch catalog error: ${resp.status} ${resp.statusText} - ${errorBody}`
      );
    }
    return await resp.json();
  },

  async fetchAttributeOptions(dbName = "test") {
    const url = `https://get-manor-frontend-reference-data-998838505944.europe-west6.run.app?data=enhancedOnly&dbName=${dbName}`;

    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(
        `Fetch attributes error: ${resp.status} ${resp.statusText}`
      );
    }
    return await resp.json();
  },

  async fetchTopLevelCounts(dbName = "test") {
    const url = `https://get-manor-frontend-reference-data-998838505944.europe-west6.run.app?data=topLevelCounts&dbName=${dbName}`;

    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(
        `Fetch top-level counts error: ${resp.status} ${resp.statusText}`
      );
    }
    return await resp.json();
  },

  async fetchTaxonomyFilterData(dbName = "test") {
    const url = `https://get-manor-frontend-reference-data-998838505944.europe-west6.run.app?data=taxonomies&dbName=${dbName}`;

    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(
        `Fetch taxonomies error: ${resp.status} ${resp.statusText}`
      );
    }
    return await resp.json();
  },

  /**
   * NEW: Fetches dynamic, filtered counts for the filter sidebar.
   */
  async fetchFilteredOptions(payload) {
    const {
      selectedFilters = {},
      brandSearch = "",
      alliumSearch = "",
      webCategorySearch = "",
      selectedAttrId = "",
      selectedAttrValue = "",
      dbName = "test",
    } = payload;

    const url = `${API_ATTR_OPTIONS}?collectionName=processed_products&filteredCounts=true&dbName=${dbName}`;

    const apiPayload = {
      topFilters: [],
      brandSearch,
      alliumSearch,
      webCategorySearch,
      attrName: selectedAttrId,
      attrValue: selectedAttrValue,
    };

    for (const [key, values] of Object.entries(selectedFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          apiPayload.topFilters.push({ key, value });
        });
      }
    }

    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(apiPayload),
    });

    if (!resp.ok) {
      throw new Error(
        `Fetch filtered options error: ${resp.status} ${resp.statusText}`
      );
    }
    const jsonResponse = await resp.json();
    return jsonResponse.data;
  },

  async fetchAttributeTaxonomies() {
    const url =
      "https://get-taxonomies-998838505944.europe-west6.run.app/download" +
      "?type=attribute_taxonomy&file_format=json";
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(
        `Fetch top-level counts error: ${resp.status} ${resp.statusText}`
      );
    }
    const result = await resp.json();

    return result;
  },

  async fetchBackendCategoryTaxonomy(dbName = "production") {
    const url =
      "https://get-taxonomies-998838505944.europe-west6.run.app/download" +
      `?taxonomy_type=category&file_format=json&environment=${dbName}`;
    
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(
        `Fetch backend category taxonomy error: ${resp.status} ${resp.statusText}`
      );
    }
    const result = await resp.json();
    
    if (result && result.data) {
      const allCategoryKeys = Object.keys(result.data);
      const subcategoryNames = new Set();
      
      // Collect all subcategory names from nested structures
      Object.values(result.data).forEach(category => {
        if (category.subcategories && Array.isArray(category.subcategories)) {
          category.subcategories.forEach(sub => {
            if (sub.name) {
              subcategoryNames.add(sub.name);
            }
          });
        }
      });
      
      // Only convert non-subcategories to root level
      const rootCategories = allCategoryKeys
        .filter(key => {
          const category = result.data[key];
          const categoryName = category.name || key;
          return !subcategoryNames.has(categoryName) && !subcategoryNames.has(key);
        })
        .map(key => {
          const value = result.data[key];
          return {
            name: value.name || key,
            subcategories: value.subcategories || [],
            display_name: value.display_name,
            description: value.description
          };
        });
      
      return rootCategories;
    }
    
    return [];
  },

  async saveProduct(product, userEmail) {
    const response = await fetch(SAVE_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Requesting-User-Email": userEmail,
      },
      body: JSON.stringify(product),
    });
    if (!response.ok) {
      throw new Error(
        `Save error: ${response.status} ${await response.text()}`
      );
    }
    return await response.json();
  },

  async saveNote(productId, note, dbName = "test") {
    const notePayload = {
      _id: productId,
      dbName,
      // Use a special property or approach that you parse on the backend
      $set: { "alliumInternalData.notes": note },
    };

    const response = await fetch(SAVE_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(notePayload),
    });

    if (!response.ok) {
      throw new Error(
        `Save note error: ${response.status} ${await response.text()}`
      );
    }
    return await response.json();
  },

  // Fetch by EAN => uses "variantNumber" param in the body
  async fetchProcessedProductByEan(ean, dbName = "test") {
    const payload = {
      page: 1,
      pageSize: 1,
      collectionName: "processed_products",
      variantNumber: ean,
      dbName,
    };

    const resp = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      throw new Error(
        `Processed fetch error (EAN): ${resp.status} ${resp.statusText}`
      );
    }
    const data = await resp.json();
    if (!data.products?.length) {
      return null;
    }
    return data.products[0];
  },

  // ---------------------------------------------------------
  // NEW: fetchCoverage => calls your "?coverage=true&db=..."
  // ---------------------------------------------------------
  async fetchCoverage(dbName = "test", removeMissingImages = true) {
    // Use GET with query params
    const url = `${API_ENDPOINT}?coverage=true&db=${dbName}&removeMissingImages=${removeMissingImages}`;

    const resp = await fetch(url, { method: "GET" });
    if (!resp.ok) {
      throw new Error(`fetchCoverage error: ${resp.status} ${resp.statusText}`);
    }

    // The response should be { coverage: [...], metadata: {...} }
    return await resp.json();
  },

  async reprocessProductsByFilter(payload) {
    const {
      selectedIds = [],
      selectedFilters = {},
      removeMissingImages = false,
      brandSearch = "",
      alliumSearch = "",
      webCategorySearch = "",
      selectedAttrId = "",
      selectedAttrValue = "",
      attrExists = null,
      dbName = "test",
      userEmail = "unknown",
      showAttributeFlags = false,
      partialProcessingSteps = [],
    } = payload;

    const topFilters = [];
    for (const [key, values] of Object.entries(selectedFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          topFilters.push({ key, value });
        });
      }
    }

    const apiPayload = {
      topFilters,
      removeMissingImages,
      brandSearch,
      alliumSearch,
      webCategorySearch,
      dbName,
      selectedIds,
      userEmail,
      attrName: selectedAttrId,
      attrValue: selectedAttrValue,
      attrExists,
      showAttributeFlags,
      partialProcessingSteps,
    };

    // Only add the partial processing steps if the user selected any.
    if (partialProcessingSteps && partialProcessingSteps.length > 0) {
      apiPayload.partialProcessingSteps = {
        reprocess: partialProcessingSteps,
      };
    }

    const response = await fetch(REPROCESS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(apiPayload),
    });

    // Use a consistent error handling pattern.
    const result = await response.json();
    if (!response.ok) {
      throw new Error(
        result.error || "An unknown error occurred during reprocessing."
      );
    }
    return result;
  },
};

export default productService;

export function flattenTaxonomies(raw) {
  if (!raw?.data) return [];

  // The top-level keys in the `data` object are the attribute IDs (e.g., "60001002")
  const attributeDefinitions = Object.entries(raw.data);

  return attributeDefinitions.map(([attributeId, attrDef]) => {
    // The correct values are in the `attrDef.values` OBJECT, not the valuesArray.
    // We get the unique 8-digit value ID from the KEY of each entry in this object.
    const processedValues = attrDef.values
      ? Object.entries(attrDef.values).map(([valueId, valueDef]) => ({
          id: valueId,
          code: String(valueId), // The unique 8-digit ID
          labels: {
            en: valueDef.value_en,
            de: valueDef.value_de,
            fr: valueDef.value_fr,
            it: valueDef.value_it,
          },
        }))
      : [];

    return {
      id: attrDef.attribute_id || attributeId, // Use the explicit ID if it exists
      code: attrDef.name,
      group: attrDef.attribute_group,
      label: attrDef.display_name?.en ?? attrDef.name,
      level: attrDef.attribute_level,
      isClosed: attrDef.is_closed_list,
      values: processedValues,
    };
  });
}
